import React, { useEffect, useState } from 'react';
import { BaseControlProps } from "../../models/BaseControlProps";
import { ReactComponent as SvgLogo } from "../../components/Header/logo.svg";
import { AuthUserResetPasswordRequest } from "../../models/AuthUserResetPasswordRequest";
import { UtilService } from '../../services/UtilService';
import { AuthAPIService } from '../../services/AuthAPIServices';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { Box, Button, CircularProgress, FormControl, FormHelperText, FormLabel, TextField, Typography } from '@mui/material';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
      'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
      width: '450px',
    },
    ...theme.applyStyles('dark', {
      boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
  }));
  
const RegisterContainer = styled(Stack)(({ theme }) => ({
    padding: 20,
    marginTop: '10vh',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0
    },
}));

const ResetPassword = (props: BaseControlProps) => {
    const [scope, apply] = React.useState<AuthUserResetPasswordRequest>({
        newPassword: "",
        confirmPassword: "",
        encodedGuid: "",
        encodedUserId: ""
    })
    const authService = new AuthAPIService();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');
    const [inputValid, isInputValid] = React.useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const Guid = searchParams.get('Guid');
        const Uid = searchParams.get('UId');
        scope.encodedGuid = Guid ? Guid : '';
        scope.encodedUserId = Uid ? Uid : '';
        applyScope();

        if (!Guid || !Uid) {
            navigate('/login');
        }

    }, [searchParams, navigate]);

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    const resetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!inputValid) { return; }
        setLoading(true);
        authService.resetPassword(scope).then((response)=>{
            setLoading(false);
            if(response.error){
                if (props.isMessageInfoShow && props.setMessageInfo) {
                    props.setMessageInfo(UtilService.getErrorMessageInfo(response.error));
                    props.isMessageInfoShow(true);
                }
            }
            else{
                if(response.response?.reset){
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getSucessMessageInfo("Password reseted successfully! Please login to proceed."));
                        props.isMessageInfoShow(true);
                    }

                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                }
                else{
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getErrorMessageInfo("We are sorry password reset failed, please contact Mercer"));
                        props.isMessageInfoShow(true);
                    }
                }
            }
        })
    }

    const validateInputs = () => {
        const newPassword = document.getElementById('newPassword') as HTMLInputElement;
        const confirmPassword = document.getElementById('confirmPassword') as HTMLInputElement;
        const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_#])[A-Za-z\d@$!%*?&_#]{8,}$/;
        scope.newPassword = newPassword.value;
        scope.confirmPassword = confirmPassword.value;
        applyScope();
        let isValid = true;
    
        if (!newPassword.value || newPassword.value.length < 8) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must be at least 8 characters long.');
          isValid = false;
        } else if (!passwordPattern.test(newPassword.value)) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.');
          isValid = false;
        } else {
          setPasswordError(false);
          setPasswordErrorMessage('');
        }
    
        if (!confirmPassword.value || confirmPassword.value !== newPassword.value) {
          setConfirmPasswordError(true);
          setConfirmPasswordErrorMessage('New Password and Confrim Password does not match.');
          isValid = false;
        } else {
          setConfirmPasswordError(false);
          setConfirmPasswordErrorMessage('');
        }
        
        if (!isValid) {
            isInputValid(false);
        } else {
            isInputValid(true);
        }

        return isValid;
      };


    return(
        <RegisterContainer direction="column" justifyContent="space-between">
            <Card variant='outlined'>
                <div className="logo-container">
                    <SvgLogo focusable={false} viewBox="0 0 500 150" />
                </div>
                <Typography
                    component="h1"
                    variant="h6"
                >
                    Reset password
                </Typography>
                <Box
                    component='form'
                    onSubmit={resetPassword}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2}}
                >
                    <FormControl>
                        <FormLabel id="passwordLabel" htmlFor="password">Password:</FormLabel>
                        <TextField
                            required
                            fullWidth
                            id="newPassword"
                            name="newPassword"
                            placeholder="••••••••"
                            type="password"
                            variant="outlined"
                            error={passwordError}
                            inputProps={{
                                'aria-label': '••••••••',
                            }}
                            helperText={passwordErrorMessage}
                            color={passwordError ? 'error' : 'primary' }
                        />
                        <FormHelperText>
                            <i>(Password must be minimum 8 characters with at least 1 uppercase letter, 1 lower case letter, 1 number, 1 special character)</i>
                        </FormHelperText>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="confirmPassword">Confirm your password:</FormLabel>
                        <TextField
                            required
                            fullWidth
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="••••••••"
                            type="password"
                            variant="outlined"
                            error={confirmPasswordError}
                            helperText={confirmPasswordErrorMessage}
                            color={confirmPasswordError ? 'error' : 'primary' }
                        />
                    </FormControl>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        onClick={validateInputs}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                            '&.Mui-disabled': {
                                color: (theme) => theme.palette.primary.contrastText,
                            }
                        }}
                    >
                        {loading ? 'Loading...' : 'Reset Password'}
                    </Button>
                </Box>
            </Card>
        </RegisterContainer>

    )
}

export default ResetPassword;